import React from "react";
import PropTypes from "prop-types";
import ButtonBase from "@material-ui/core/ButtonBase";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import RoundedAvatar from "../../../waybee-ui/RoundedAvatar";

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(222px, 1fr));
  gap: 22px;
  width: 100%;
  padding: 8px 0 4px;
`;

const Card = styled(ButtonBase)`
  && {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    min-width: 0;
    padding: 26px 16px 22px;
    border: 1px solid #eeeeee;
    background: #ffffff;
    text-align: center;
    cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
    transition: background-color 0.15s ease, border-color 0.15s ease;
  }
  &&:hover {
    background: ${({ $clickable }) =>
      $clickable ? "rgba(0, 0, 0, 0.04)" : "#ffffff"};
    border-color: ${({ $clickable, theme }) =>
      $clickable ? theme.color.primary.light : "#eeeeee"};
  }
`;

const Meta = styled.div`
  margin-top: 18px;
  width: 100%;
  min-width: 0;
`;

const MetaLine = styled.p`
  margin: 0 0 4px;
  font-size: 16px;
  line-height: 1.3;
  font-weight: 400;
  color: ${({ theme }) => theme.color.default.main};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NameLine = styled(MetaLine)`
  margin-top: 6px;
  margin-bottom: 0;
  font-size: 18px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.secondary.dark};
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const ClassStudentsListViewGrid = ({ students, onStudentClick, canOpenStudent }) => {
  const { t } = useTranslation();

  return (
    <Grid>
      {students.map(student => {
        const clickable = Boolean(canOpenStudent);
        const callNumber =
          student.rollCallNum != null && student.rollCallNum !== ""
            ? student.rollCallNum
            : "—";

        return (
          <Card
            key={student.ra}
            $clickable={clickable}
            disableRipple={!clickable}
            onClick={() => {
              if (clickable) onStudentClick(student);
            }}
            aria-label={student.name}
          >
            <RoundedAvatar
              imageSrc={student.photo}
              imageAlt={student.name}
              size={148}
            >
              {student.name}
            </RoundedAvatar>
            <Meta>
              <MetaLine>
                {t("common.labels.callNumberShort")}
                {" "}
                {callNumber}
              </MetaLine>
              <MetaLine>{t("classes.details.ra", { ra: student.ra })}</MetaLine>
              <NameLine title={student.name}>{student.name}</NameLine>
            </Meta>
          </Card>
        );
      })}
    </Grid>
  );
};

ClassStudentsListViewGrid.propTypes = {
  students: PropTypes.arrayOf(
    PropTypes.shape({
      ra: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string,
      photo: PropTypes.string,
      rollCallNum: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ),
  onStudentClick: PropTypes.func,
  canOpenStudent: PropTypes.bool
};

ClassStudentsListViewGrid.defaultProps = {
  students: [],
  onStudentClick: () => {},
  canOpenStudent: false
};

export default ClassStudentsListViewGrid;
